import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { ErrorResponse } from 'hx-services';
import { ErrorHandlerService } from '@callcenter-app/service/error-handler.service';

/**
 * The one way the current-orders components report a failed request: a business error (HTTP 570, `{message: <key>}`)
 * goes through the app's `ErrorHandlerService` (it translates the key); anything else — network drop, a gateway
 * page, an empty body — is the generic «Произошла ошибка». Never throws on a body that is not the usual JSON.
 */
export function reportHttpError(err: HttpErrorResponse, errorService: ErrorHandlerService, toastr: ToastrService, tr: TranslocoService): void {
  if (businessMessage(err)) {
    errorService.handler(err.error as ErrorResponse);
  } else {
    toastr.error(tr.translate('unexpectedError'));
  }
}

/** The business-error key the backend sent (`ordersCurrent.*`, `ordersArchive.*`, `operation.forbidden`…), if there is one. */
export function businessMessage(err: HttpErrorResponse | undefined): string | undefined {
  const body: unknown = err?.error;
  if (body !== null && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string') {
    return (body as { message: string }).message;
  }
  return undefined;
}

/**
 * The transaction guard's answer, `ordersCurrent.actionNotAllowed{action, reason: RETRY}`: the order's transaction could not go on for a
 * moment and nothing was written — «the order is being updated, try again in a minute». Any action of the page can get it; the `action`
 * does not matter, the reason alone does. Never throws on a body that is not the usual JSON.
 */
export function isRetryRefusal(err: HttpErrorResponse | undefined): boolean {
  if (businessMessage(err) !== 'ordersCurrent.actionNotAllowed') {
    return false;
  }
  const data: unknown = (err?.error as { data?: unknown } | null | undefined)?.data;
  return data !== null && typeof data === 'object' && (data as { reason?: unknown }).reason === 'RETRY';
}
